import { z } from 'zod';
import { isRichTextEmpty } from './utils.js';
/**
 * Plain-string category, not a native Postgres/Prisma enum, so a future category (e.g.
 * "Documentation") is a one-line const change and Zod schema update, not a migration.
 */
export const FEEDBACK_CATEGORY = {
    ALPHA_TESTING: 'ALPHA_TESTING',
    BUG: 'BUG',
    FEATURE_REQUEST: 'FEATURE_REQUEST',
    GENERAL: 'GENERAL',
};
export const FeedbackCategorySchema = z.enum(Object.values(FEEDBACK_CATEGORY));
/**
 * Assigned alpha-test goals, only relevant when {@link FEEDBACK_CATEGORY.ALPHA_TESTING}
 * is selected. Named distinctly from `FEEDBACK_CATEGORY` (not `ALPHA_TESTING_GOAL`) so
 * the two constants don't read as duplicates of each other.
 */
export const ALPHA_TEST_GOAL = {
    CREATE_ACCOUNT: 'CREATE_ACCOUNT',
    CREATE_CLIENTS: 'CREATE_CLIENTS',
    CREATE_LINE_ITEMS: 'CREATE_LINE_ITEMS',
    CREATE_QUOTES: 'CREATE_QUOTES',
    CONVERT_QUOTE_TO_INVOICE: 'CONVERT_QUOTE_TO_INVOICE',
    CREATE_INVOICES: 'CREATE_INVOICES',
    CREATE_EXPENSES: 'CREATE_EXPENSES',
    OTHER: 'OTHER',
};
export const AlphaTestGoalSchema = z.enum(Object.values(ALPHA_TEST_GOAL));
/** Display labels for the goal dropdown — kept alongside the const it labels. */
export const ALPHA_TEST_GOAL_LABELS = {
    CREATE_ACCOUNT: 'Create an account',
    CREATE_CLIENTS: 'Create 5 clients',
    CREATE_LINE_ITEMS: 'Create 5 line items',
    CREATE_QUOTES: 'Create 5 quotes',
    CONVERT_QUOTE_TO_INVOICE: 'Turn a quote into an invoice',
    CREATE_INVOICES: 'Create 5 invoices',
    CREATE_EXPENSES: 'Create 5 expenses',
    OTHER: 'Other / not listed',
};
/**
 * Prompt shown for the rating-reason field, keyed by the 1-5 emoji rating value. Shared
 * between the feedback widget and the notification email so both ask the same question.
 */
export const RATING_REASON_LABELS = {
    1: 'What did you find frustrating about this experience?',
    2: 'Why were you disappointed?',
    3: 'What could we improve on this experience?',
    4: 'What felt good about this experience?',
    5: 'What did you enjoy most about this experience?',
};
export const UserFeedbackSubmissionSchema = z
    .object({
    // Coerced rather than z.number() — the backend controller validates this schema
    // directly against multipart/form-data field values, which arrive as strings.
    rating: z.coerce.number().int().min(1).max(5),
    category: FeedbackCategorySchema,
    goal: AlphaTestGoalSchema.optional(),
    /**
     * Only relevant for {@link FEEDBACK_CATEGORY.ALPHA_TESTING} — arrives as the string
     * "true"/"false" over multipart/form-data, so it's parsed rather than coerced
     * (`z.coerce.boolean()` would treat the string "false" as truthy).
     */
    taskCompleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => (value === undefined ? undefined : value === 'true')),
    primaryDetail: z.string().optional(),
    secondaryDetail: z.string().optional(),
    /** Free-text follow-up, only shown/used for an incomplete alpha-testing task. */
    additionalThoughts: z.string().optional(),
    /**
     * Why the tester picked the rating they did — prompt depends on the value, see
     * {@link RATING_REASON_LABELS}.
     */
    ratingReason: z.string().optional(),
    /** Current SPA route, captured automatically by the widget, not typed by the user. */
    pageUrl: z.string(),
})
    .superRefine((submission, ctx) => {
    const isAlpha = submission.category === FEEDBACK_CATEGORY.ALPHA_TESTING;
    if (isAlpha && submission.goal === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Goal is required for alpha testing feedback',
            path: ['goal'],
        });
    }
    if (isAlpha && submission.taskCompleted === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Please indicate whether you were able to complete the task',
            path: ['taskCompleted'],
        });
    }
    // Non-alpha categories always need both guided fields; an alpha-testing submission
    // only needs them when the task wasn't completed — nothing else to fill in otherwise.
    const requiresGuidedDetails = !isAlpha || submission.taskCompleted === false;
    if (requiresGuidedDetails && isRichTextEmpty(submission.primaryDetail)) {
        ctx.addIssue({
            code: 'custom',
            message: 'This field is required',
            path: ['primaryDetail'],
        });
    }
    if (requiresGuidedDetails && isRichTextEmpty(submission.secondaryDetail)) {
        ctx.addIssue({
            code: 'custom',
            message: 'This field is required',
            path: ['secondaryDetail'],
        });
    }
});
/**
 * Accepted screenshot size — bounds both an auto-captured `html2canvas` PNG and a
 * user-selected replacement file. Shared so the frontend can validate before upload and
 * the backend can enforce the same ceiling server-side.
 */
export const SCREENSHOT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** Accepted screenshot MIME types — shared between the upload widget and the API. */
export const SCREENSHOT_ALLOWED_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
];
//# sourceMappingURL=user-feedback.js.map